/**
 * WHICH DAY IT IS, for anything that resets daily.
 *
 * THE DAY USED TO END AT UTC MIDNIGHT, which is 8pm in New York and 7pm once
 * the clocks go back. Almost everybody playing is on Eastern time, so the
 * daily rolled over during their evening: somebody who played at nine on
 * Monday night and came back at ten on Tuesday morning was handed the same
 * puzzle both times and told they had already played it. Reported exactly that
 * way -- "the one I was testing yesterday was still the same today".
 *
 * The puzzle was never wrong. `dailySeedFor` hashes the date and three dates
 * give three different seeds; the date itself was the wrong date.
 *
 * ONE FUNCTION, BOTH SIDES. The gateway decides which puzzle to serve and
 * whether an attempt has been spent, and the client decides whether to say
 * "new today" -- so a disagreement about the date shows up as a card offering
 * a daily the server then refuses. There is deliberately nowhere else to
 * compute this.
 *
 * A FIXED ZONE RATHER THAN THE VIEWER'S OWN. A daily is worth something
 * because everybody got the same puzzle, and a boundary that moves with the
 * reader would give somebody in Sydney tomorrow's puzzle this afternoon and
 * split the leaderboard down the middle. One zone, named here, is the price of
 * one shared board -- and DST is handled for us, which is why this is `Intl`
 * and not an offset in hours.
 */

/** Where the day turns over. Chosen because that is where the players are. */
export const DAILY_TIME_ZONE = 'America/New_York';

/*
 * Built once. Constructing a formatter is expensive enough that doing it per
 * call would show up on a route that answers three of these per request.
 */
const DAY_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: DAILY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The `YYYY-MM-DD` a moment falls in, in the daily's zone.
 *
 * Assembled from parts rather than trusting a locale to format ISO-shaped
 * dates: `en-CA` happens to produce exactly this today, and a locale data
 * update is not something this should be able to lose to.
 */
export function gameDayKey(at: number | Date = Date.now()): string {
  const parts = DAY_PARTS.formatToParts(typeof at === 'number' ? new Date(at) : at);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
